import InsRequest from "@/api/ins/base";
import { insSignBody } from "@/api/utils/api_encrypt";
import {
  LOGIN_URL,
  LOGIN_FB_URL,
  LOGIN_WITH_TWO_FACTORURL,
  INS_BASE_URL,
  SEND_TWOFACTOR_LOGINSMS_URL
} from "../base/ins_api_const";
import { getUUid } from "@/api/utils/api_helpers";
import InsLoginCookieHandle from "@/api/ins/cookie_manager/login_cookie";
import * as ins_event_const from "@/api/ins_api/ins_api_consts/ins_event_const";

/**
 * transformChallengeURLString
 * @param urlString
 * @param operators
 */
function transformChallengeURLString(urlString: string, operators?: string) {
  const index = urlString.indexOf("challenge");
  let path = urlString.substring(
    operators ? index + "UrlParser".length + 1 : index
  );
  if (path.search(/(.+?\/$)/) === -1) {
    path += "/";
  }
  const newPath = operators
    ? `${INS_BASE_URL}api/v1/challenge/${operators}/${path}`
    : `${INS_BASE_URL}api/v1/${path}`;
  return newPath;
}
/**
 * [Login by ins]
 * @param userName
 * @param passWord
 */
export const loginByIns = (userName: string | number, passWord: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      // 注意：登陆前清除原生缓存的cookie
      InsLoginCookieHandle.createCookie(userName); // 创建cookie单例

      const uid = await getUUid();

      const loginInfo = {
        reg_login: "0",
        username: userName,
        password: passWord,
        device_id: uid,
        phone_id: uid,
        login_attempt_count: "0"
      };
      const data: any = await insSignBody(loginInfo);
      const result = await InsRequest.post(
        LOGIN_URL,
        {
          data
        },
        {
          is_login: true,
          default_headers: true
        },
        ins_event_const.INS_API_LOGIN
      );

      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * [Login Fb]
 * @param accessToken
 */
export const loginByFb = (accessToken: any) =>
  new Promise(async (resolve, reject) => {
    try {
      const uid: any = await getUUid();
      const uuid32 = uid.toLowerCase().replace(/-/g, "");
      const loginInfo = {
        dryrun: "true",
        waterfall_id: uuid32,
        device_id: uid,
        allow_contacts_sync: "false",
        fb_access_token: accessToken,
        phone_id: uid
      };
      const data: any = await insSignBody(loginInfo);
      const result = await InsRequest.post(
        LOGIN_FB_URL,
        {
          data
        },
        {
          is_login: true,
          default_headers: true
        },
        ins_event_const.INS_API_FACEBOOK_LOGIN
      );
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });

/**
 * verifyChallenge
 * @param url
 * @param code
 */
export const verifyChallenge = (url: string, code: string) =>
  new Promise(async (resolve, reject) => {
    try {
      const uid = await getUUid();
      const info = {
        device_id: uid,
        security_code: code
      };
      const data: any = await insSignBody(info);
      const postUrl = transformChallengeURLString(url);
      const result = await InsRequest.post(
        postUrl,
        {
          data
        },
        {
          is_login: true,
          default_headers: true
        },
        ins_event_const.INS_API_VERIFY_CHALLENGE
      );
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });

/**
 * verifyTwoFactor
 * @param twoFactorInfo
 * @param verificationCode
 */
export const verifyTwoFactor = (
  username: string,
  two_factor_identifier: string,
  verificationCode: any
) =>
  new Promise(async (resolve, reject) => {
    try {
      const uid = await getUUid();
      const loginInfo = {
        verification_code: verificationCode,
        username,
        device_id: uid,
        two_factor_identifier
      };
      const data: any = await insSignBody(loginInfo);
      const result = await InsRequest.post(
        LOGIN_WITH_TWO_FACTORURL,
        {
          data
        },
        {
          is_login: true,
          default_headers: true
        },
        ins_event_const.INS_API_TWO_FACTOR_LOGIN
      );
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });

/**
 * Resend two facotor code
 * @param twoFactorInfo
 */
export const twoFactorResend = (
  username: string,
  two_factor_identifier: string
) =>
  new Promise(async (resolve, reject) => {
    try {
      const uid = await getUUid();
      const uuid32 = uid.toLowerCase().replace(/-/g, "");
      const loginInfo = {
        _uuid: uid,
        username,
        device_id: uid,
        two_factor_identifier,
        waterfall_id: uuid32
      };
      const data: any = await insSignBody(loginInfo);
      const result = await InsRequest.post(
        SEND_TWOFACTOR_LOGINSMS_URL,
        {
          data
        },
        {
          is_login: true,
          default_headers: true
        },
        ins_event_const.INS_API_SEND_TWO_FACTOR_LOGIN_SMS
      );
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });

/**
 * sendChallenge
 * @param url
 * @param choice
 */
export const sendChallenge = (url: string, choice: string) =>
  new Promise(async (resolve, reject) => {
    try {
      const uid = await getUUid();
      const info = {
        device_id: uid,
        choice,
        _uuid: uid
      };
      const data: any = await insSignBody(info);
      const result = await InsRequest.post(
        transformChallengeURLString(url),
        {
          data
        },
        {
          is_login: true,
          default_headers: true
        },
        ins_event_const.INS_API_SELECT_CHALLENGE
      );
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });

/**
 * POST /api/v1/challenge/reset/{user_id}/{nonce_code}/
 * @param url
 * @returns {Promise<any>}
 */
export const resetChallenge = (url: string) =>
  new Promise(async (resolve, reject) => {
    try {
      const info = { device_id: await getUUid() };
      const data: any = await insSignBody(info);
      const result = await InsRequest.post(
        transformChallengeURLString(url, "reset"),
        {
          data
        },
        {
          is_login: true,
          default_headers: true
        },
        ins_event_const.INS_API_RESET_CHALLENGE
      );
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });

/**
 * POST /api/v1/challenge/replay/{user_id}/{nonce_code}/
 * signed_body:{"phone_number":null,"device_id":...,"choice":"你之前选择的choice"}
 * @param url
 * @param choice
 * @returns {Promise<any>}
 */
export const replayChallenge = (url: string, choice: string) =>
  new Promise(async (resolve, reject) => {
    try {
      const uid = await getUUid();
      const info = {
        device_id: uid,
        choice,
        _uuid: uid
      };
      const data: any = await insSignBody(info);
      const result = await InsRequest.post(
        transformChallengeURLString(url, "replay"),
        { data },
        {
          is_login: true,
          default_headers: true
        },
        ins_event_const.INS_API_REPLAY_CHALLENGE
      );
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });

/**
 * fetchChallenge
 * @param url
 */
export const fetchChallenge = (url: string) =>
  new Promise(async (resolve, reject) => {
    try {
      const uid = await getUUid();
      const info = { device_id: uid };
      const result = await InsRequest.get(
        transformChallengeURLString(url),
        {
          params: info
        },
        {
          is_login: true,
          default_headers: true
        },
        ins_event_const.INS_API_FETCH_CHALLENGE
      );
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
